import React, { useEffect, useRef, useState } from 'react';
import { AnyIfEmpty, useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

// Redux Actions
import { getLocationDetailById } from '../../../redux/location/actions/location.action';
import { getLocationUnitsList, resetLocationUnitsList } from '../../../redux/location-units-list/actions/locationUnitsList.action';
import { removePreviousUnits, syncUnits } from '../../../redux/units/actions/unitsByLocationId';
import { AppDispatch, IRootState } from '../../../redux/rootReducer';

// Components
import AddUnits from '../../units/addunits/AddUnits';
import AddVendorsAppointment from '../../vendors/addVendors/AddVendorsAppointment';
import CheckIcon from '../../svgIcons/CheckIcon';
import CrossIcon from '../../svgIcons/CrossIcon';
import CustModal from '../../common/custmodal/CustModal';
import DeleteIcon from '../../svgIcons/DeleteIcon';
import EditIcon from '../../svgIcons/EditIcon';
import Loader from '../../common/loader/Loader';
import MapIcon from '../../svgIcons/MapIcon';
import RemoveModal from '../../common/removeModal/RemoveModal';
import ShareLocation from '../sharelocation/ShareLocation';
import { SliderWithLightbox } from '../../common/slider-with-lightbox/SliderWithLightbox';
import TinyLoader from '../../common/tiny-loader/TinyLoader';
import Toast from '../../common/toast/Toast';
import Tooltip from '../../common/tooltip/Tooltip';
import { PMSIntegrationVendor, UnitStatus } from '../../units/unitscard/Unitscard';

// Utils & Constants
import { getLocationTimeZone } from '../../../constants/constants';
import { hasPermission } from '../../auth/login/Login';
import { useDebounce } from '../../../utils/useDebounce';

// Images & Icons
import banner from '../../../images/no-unit-image.svg';
import iconBack from '../../../images/icon-chevron-left.svg';
import iconCopy from '../../../images/icon-copy-2.svg';
import iconDelete from '../../../images/icon-delete.svg';
import iconEdit from '../../../images/icon-edit.svg';
import iconLocation from '../../../images/icon-location.svg';
import iconTimezone from '../../../images/icon-timezone.svg';
import NoUnits from '../../../images/no-units-found.svg';
import SearchIcon from '../../../images/icon-search.svg';

// Styles
import styles from './LocationDetail.module.css';

const locationFilter = [
    { label: 'All', value: 'all' },
    { label: 'Available', value: 'available' },
    { label: 'Archived', value: 'archived' },
    { label: 'Manual', value: 'manual' },
    { label: 'Occupied', value: 'occupied' },
];


export interface unitsDataInterface {
    id: string;
    created_at: Date;
    updated_at: Date;
    price: number;
    name: string;
    size: number;
    rooms?: number;
    baths: number;
    status?: number;
    nickName?: string;
    locationId: string;
    deleteImage_url: string;
    adminId: string;
    image_url: string;
    unitYardiId: string;
    availabilityDate: Date;
    unitOccupancyStatus: string;
    unitLeasedStatus: string;
    locationName: string;
    description?: string | null;
    unit_status: UnitStatus;
    buildingName: string | null;
    integrationVendor: PMSIntegrationVendor;
    unitRentReady: string;
}

const LocationDetail = () => {
    const dispatch: AppDispatch = useDispatch();
    const navigate = useNavigate()
    // const [selectedFilter, setSelectedFilter] = useState({ value: '', label: '' });

    const canDeleteProperty = hasPermission('Properties & Units', 'Property', 'delete');
    const canReadProperty = hasPermission('Properties & Units', 'Property', 'read');
    const canUpdateProperty = hasPermission('Properties & Units', 'Property', 'update');
    const canCreateUnit = hasPermission('Properties & Units', 'Unit', 'create');
    const canReadUnit = hasPermission('Properties & Units', 'Unit', 'read');

    const { id } = useParams();
    const path = useLocation().pathname;

    type LocationState = { search: string; unitSearch?: string };

    const { search, unitSearch } = (useLocation().state || false) as LocationState;
    const observer: AnyIfEmpty<object> = useRef();
    const baseUrl = import.meta.env.VITE_APP_VISITOR_URL;
    const removeUnitsLoading = useSelector((state: IRootState) => state.units.unitsLoading);
    const syncUnitLoading = useSelector((state: IRootState) => state.unitsByLocationId.syncUnit.loading);

    // Location detail by id
    const {
        loading: locationDetailLoading,
        data: locationDetail,
        errorMsg: locationDetailError,
    } = useSelector((state: IRootState) => state.locationReducer.locationDetail);

    const [shareProperty, setShareProperty] = useState(false);
    const [addUnit, setAddUnit] = useState(false);
    const [addVendor, setAddvendor] = useState(false);
    const [deletePopup, setDeletePopup] = useState(false);
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');

    // This was used to show slider for invited staff
    // const [showShareLocationSlider, setShowShareLocationSlider] = useState(false);

    const [searchUnits, setSearchUnits] = useState(unitSearch || '');
    const [currentPage, setCurrentPage] = useState(1);
    const previousSearchRef = useRef<string>('');
    const previousLocationIdRef = useRef<string | undefined>(undefined);
    const [startIndex, setStartIndex] = useState(0);
    const [showLightBox, setShowLightBox] = useState(false);
    const [toastType, setToastType] = useState('delete');
    const [initialized, setInitialized] = useState(false);
    const hasFetched = useRef(false); // Prevents multiple API calls
    const searchWithDebounce = useDebounce(searchUnits.trim(), 1000, setCurrentPage);
    const [selectedUnitForEdit, setSelectedUnitForEdit] = useState<unitsDataInterface | null>(null);
    const [selectedUnitForDelete, setSelectedUnitForDelete] = useState<unitsDataInterface | null>(null);
    const [editUnitModal, setEditUnitModal] = useState(false);
    const [deleteUnitModal, setDeleteUnitModal] = useState(false);
    const tinyLoaderRef = useRef<HTMLTableCellElement>(null);

    // Get units list from Redux
    const {
        loading: unitsListLoading,
        data: unitsData,
        count: totalCount,
        error: unitsListError,
    } = useSelector((state: IRootState) => state.locationUnitsList);

    const hasMore = unitsData.length > 0 && unitsData.length < totalCount;

    const canDeleteUnit = hasPermission('Properties & Units', 'Unit', 'delete');
    const canUpdateUnit = hasPermission('Properties & Units', 'Unit', 'update');

    const initLocationDetail = async (id: string) => {
        if (!hasFetched.current) {
            hasFetched.current = true;
            if (canReadProperty) await dispatch(getLocationDetailById(id));
            if (canReadUnit) await dispatch(syncUnits(id, 1));
            setInitialized(true);
        }
    };

    useEffect(() => {
        if (id && !initialized) {
            initLocationDetail(id);
        }
    }, [id, canReadProperty, canReadUnit]);

    const lastElement = (node: string) => {
        if (unitsListLoading) return;
        if (observer.current) observer.current.disconnect();

        observer.current = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMore) {
                    if (currentPage * 12 < totalCount) {
                        setCurrentPage((prevPage) => prevPage + 1);
                    }
                }
            },
            {
                threshold: 0.9, // Trigger when 90% of the element is visible
            }
        );
        if (node) observer.current.observe(node);
    };

    // Reset when search or locationId changes
    useEffect(() => {
        const searchChanged = previousSearchRef.current !== searchWithDebounce;
        const locationChanged = previousLocationIdRef.current !== id;

        if ((searchChanged || locationChanged) && id && initialized && canReadUnit) {
            // Reset pagination on search/location change
            setCurrentPage(1);
            previousSearchRef.current = searchWithDebounce;
            previousLocationIdRef.current = id;
        }
    }, [searchWithDebounce, id, initialized, canReadUnit]);

    // Fetch units with debouncing and pagination
    useEffect(() => {
        if (id && initialized && canReadUnit) {
            const isFirstPage = currentPage === 1;
            const shouldAppend = !isFirstPage;

            dispatch(
                getLocationUnitsList(
                    id,
                    {
                        searchText: searchWithDebounce || undefined,
                        page: currentPage,
                        limit: 12,
                    },
                    shouldAppend
                )
            );
        }
    }, [id, searchWithDebounce, currentPage, initialized, canReadUnit, dispatch]);

    // Reset units on unmount
    useEffect(() => {
        return () => {
            dispatch(resetLocationUnitsList());
        };
    }, [dispatch]);

    // Smooth scroll to bottom when loading (for pagination - only when currentPage > 1)
    useEffect(() => {
        if (unitsListLoading && currentPage > 1 && tinyLoaderRef.current) {
            // Small delay to ensure DOM is updated
            setTimeout(() => {
                tinyLoaderRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
                // scroll to bottom of the page
                window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
            }, 100);
        }
    }, [unitsListLoading, currentPage]);

    // const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    //     setSearchUnits(e.target.value);
    // };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.trimStart(); // removes starting spaces only
        setSearchUnits(value);
    };


    const clickForLightboxHandler = (index: number) => {
        setStartIndex(index);
        setShowLightBox(true);
        document.body.classList.add('disable-scrolling');
    };

    useEffect(() => {
        if (showToast) {
            setTimeout(() => {
                setShowToast(false);
                setToastMessage('');
                setToastType('delete');
            }, 4000);
        }
    }, [showToast]);

    const handleCopyLink = (url: string) => {
        navigator.clipboard
            .writeText(url)
            .then(() => {
                setShowToast(true);
                setToastMessage('Link copied to clipboard!');
                setToastType('success');
            })
            .catch(() => {
                setShowToast(true);
                setToastMessage('Failed to copy link.');
                setToastType('delete');
            });
    };

    const redirectToMap = () => {
        const pattern = /^https:\/\/app\.mappedin\.com\/map\/([^?/]+)(?:\?floor=([^&]+).*)?$/;
        const match = locationDetail?.mappedInId?.match(pattern);
        const mapId = match ? match[1] : locationDetail?.mappedInId;
        const floorId = match && match[2] ? match[2] : locationDetail?.mappedInFloorId?.replace('floor=', '') || null;

        navigate(`/location/${id}/mappedIn`, {
            state: {
                mapId: mapId,
                floorId: floorId,
                locationId: id,
                locationName: locationDetail?.name,
                openAITourVectorId: locationDetail?.openAIVectorId || null,
                openAITourFileId: locationDetail?.openAIFileId || null,
            },
        });
    };

    const redirectToPynwheelConnect = () => {
        navigate(`/location/${id}/pynwheel-connect`, {
            state: {
                pynwheelConnectUrl: locationDetail?.mappedInId,
                locationId: id,
                locationName: locationDetail?.name,
            },
        });
    };

    if (!canReadProperty || !canReadUnit) {
        navigate('/no-permission');
    }

    const refetchUnits = () => {
        if (id && initialized && canReadUnit) {
            setCurrentPage(1);
            setSearchUnits('');
            dispatch(
                getLocationUnitsList(
                    id,
                    {
                        searchText: '',
                        page: 1,
                        limit: 12,
                    },
                    false
                )
            );
        }
    };

    const onUnitClick = (unitId: string, status: UnitStatus) => {
        status === 'available'
            ? navigate(`/unitDetails/${unitId}`, { state: { locationId: id, locationSearch: search, unitSearch: searchUnits } })
            : {};
    };


    return (
        <>
            {/* below component was used to show slider of invited staff */}
            {/* <LocationInvitation
                showShareLocationSlider={showShareLocationSlider}
                setShowShareLocationSlider={setShowShareLocationSlider}
                setShowToast={setShowToast}
                setToastMessage={setToastMessage}
                setToastType={setToastType}
                isRestricted={isRestricted}
            /> */}
            {(removeUnitsLoading) && <Loader />}
            {showToast && <Toast type={toastType} message={toastMessage} />}
            <CustModal open={shareProperty} setOpen={setShareProperty} bodyData={<ShareLocation setOpen={setShareProperty} />} />
            <CustModal
                open={addUnit}
                setOpen={setAddUnit}
                bodyData={
                    <AddUnits
                        setShowToast={setShowToast}
                        setToastMessage={setToastMessage}
                        locationId={id}
                        setOpen={setAddUnit}
                        unitsData={unitsData}
                        setCurrentPage={setCurrentPage}
                        setToastType={setToastType}
                        onUnitAdded={refetchUnits}
                    />
                }
            />

            <CustModal
                open={addVendor}
                setOpen={setAddvendor}
                bodyData={
                    <AddVendorsAppointment
                        locationId={id}
                        setOpen={setAddvendor}
                        locationName={locationDetail.name}
                        setShowToast={setShowToast}
                        setToastMessage={setToastMessage}
                        setToastType={setToastType}
                    />
                }
            />

            <CustModal
                open={deletePopup}
                setOpen={setDeletePopup}
                bodyData={<RemoveModal type="Property" unitId={id} setDeletePopup={setDeletePopup} />}
            />

            <CustModal
                open={editUnitModal}
                setOpen={setEditUnitModal}
                bodyData={
                    selectedUnitForEdit && (
                        <AddUnits
                            setShowToast={setShowToast}
                            setToastMessage={setToastMessage}
                            setOpen={setEditUnitModal}
                            locationId={id}
                            unitData={selectedUnitForEdit}
                            unitsData={unitsData}
                            setToastType={setToastType}
                            onUnitAdded={refetchUnits}
                        />
                    )
                }
            />

            <CustModal
                open={deleteUnitModal}
                setOpen={setDeleteUnitModal}
                bodyData={
                    selectedUnitForDelete && (
                        <RemoveModal
                            type="Unit"
                            setDeletePopup={setDeleteUnitModal}
                            unitId={selectedUnitForDelete?.id}
                            unitData={selectedUnitForDelete}
                            locationId={id}
                            setShowToast={setShowToast}
                            setToastMessage={setToastMessage}
                            unitsData={unitsData}
                            onDeleteSuccess={refetchUnits}
                        />
                    )
                }
            />

            {syncUnitLoading || locationDetailLoading ? (
                <Loader />
            ) : (
                <div className={`${styles.pagWrapper} outletConainer`}>
                    <div className={styles.flexContainer}>
                        <div className={styles.locationDetail}>
                            <div className={styles.locationHeader}>
                                <div className={styles.wrapper}>
                                    <div
                                        className={styles.cartDetailsBack}
                                        onClick={async () => {
                                            dispatch(removePreviousUnits());
                                            dispatch(resetLocationUnitsList());
                                            const path = search ? `/locations?search=${encodeURIComponent(search)}` : '/locations';
                                            navigate(path, { replace: true });
                                        }}>
                                        <img src={iconBack} />
                                    </div>
                                    <div className={styles.headerInfo}>
                                        <div className={styles.title}>
                                            <h1>
                                                {locationDetail?.name && locationDetail?.name}
                                                {locationDetail?.provisioningId && <span>(#{locationDetail?.provisioningId})</span>}
                                                <div className={styles.locationActions}>
                                                    {canUpdateProperty && (
                                                        <img
                                                            onClick={() => {
                                                                navigate(`/edit-location/${locationDetail?.id}`, {
                                                                    state: {
                                                                        path,
                                                                    },
                                                                });
                                                            }}
                                                            src={iconEdit}
                                                        />
                                                    )}
                                                    {canDeleteProperty && (
                                                        <img onClick={() => setDeletePopup(true)} src={iconDelete} alt="delete" />
                                                    )}
                                                </div>
                                            </h1>
                                            <p>
                                                <img src={iconLocation} alt="Property" />
                                                <span className={styles.streetDetail}>
                                                    {locationDetail?.street1}
                                                    {locationDetail?.street2 && `, ${locationDetail?.street2}`}, {locationDetail?.city},{' '}
                                                    {locationDetail?.state}, {locationDetail?.zip}
                                                </span>
                                            </p>
                                            <p className={styles.w100}>
                                                <img src={iconTimezone} alt="Property" />
                                                <span className={styles.timezone}>
                                                    Timezone: {getLocationTimeZone(locationDetail?.timeZone)}
                                                </span>
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div className={styles.btnWrapper}>
                                    {locationDetail?.mappedInId?.toLowerCase().includes('mappedin') && (
                                        <button onClick={redirectToMap} className={styles.mapBtn}>
                                            <MapIcon color="#fff" />
                                            <span>Mapped In</span>
                                        </button>
                                    )}

                                    {locationDetail?.mappedInId?.toLowerCase().includes('pynwheelconnect') && (
                                        <button onClick={redirectToPynwheelConnect} className={styles.mapBtn}>
                                            <MapIcon color="#fff" />
                                            <span>Pynwheel Connect</span>
                                        </button>
                                    )}


                                    {canCreateUnit && <button onClick={() => setAddUnit(true)}>+ Add Unit</button>}
                                    {/* {canCreateVendor && <button className={styles.vendorButton} onClick={() => {
                                        dispatch(resetVendorLockerListState())
                                        setAddvendor(true)
                                    }}>+ Add Vendor Access</button>} */}
                                </div>
                            </div>
                            <div className={styles.sliderWrapper}>
                                <div className="location-detail-slider">
                                    {locationDetail?.images ? (
                                        <SliderWithLightbox
                                            imgs={locationDetail?.images}
                                            setShowLightBox={setShowLightBox}
                                            showLightBox={showLightBox}
                                            startIndex={startIndex}
                                            onClickHandler={clickForLightboxHandler}
                                        />
                                    ) : (
                                        <img src={banner} className={styles.noPropertyImg} />
                                    )}
                                </div>
                                <div className={styles.locationInfo}>
                                    <div className={`${styles.beansUrl} ${styles.info}`}>
                                        <label>Visitor Portal Link</label>
                                        <div className={styles.url}>
                                            <img
                                                src={iconCopy}
                                                alt="Property"
                                                onClick={() => {
                                                    handleCopyLink(`${baseUrl}/location/${locationDetail?.id}`);
                                                }}
                                            />
                                            <span
                                                onClick={() => {
                                                    handleCopyLink(`${baseUrl}/location/${locationDetail?.id}`);
                                                }}>
                                                Copy the marketing link to the property to your clipboard.
                                            </span>
                                        </div>
                                    </div>

                                    <div className={styles.info}>
                                        <label>Description:</label>
                                        <p>{locationDetail?.description}</p>
                                    </div>
                                    <div className={styles.info}>
                                        <label>Master Code (for internal use only)</label>
                                        <p className={styles.masterCode}>{locationDetail?.masterCode}</p>
                                    </div>
                                    <div className={styles.info}>
                                        <label>Confirmation Message:</label>
                                        <p className={styles.confirmationMessage}>{locationDetail?.confirmText}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {canReadUnit && (
                            <React.Fragment>
                                <div className={styles.searchUnits}>
                                    <div className={styles.UnitsTitle}>Units</div>
                                    <div className={styles.searchFilter}>
                                        {/* <div className={styles.filter}>
                                            <Select
                                                onChange={(selectedOption) => {
                                                    if (selectedOption?.value) {
                                                        setSelectedFilter(selectedOption);
                                                        setCurrentPage(1);
                                                    }
                                                }}
                                                isSearchable={false}
                                                classNamePrefix="custom-react-select"
                                                value={
                                                    selectedFilter?.value
                                                        ? {
                                                            label: selectedFilter?.label,
                                                            value: selectedFilter?.value,
                                                        }
                                                        : { label: 'Available', value: 'available' }
                                                }

                                                options={locationFilter}
                                                placeholder="Select Option"
                                                styles={colourStyles}
                                            />
                                        </div> */}

                                        <div className={styles.search}>
                                            <input
                                                onChange={(e) => handleChange(e)}
                                                type="text"
                                                value={searchUnits}
                                                placeholder="Search for units"
                                                maxLength={50}
                                            />
                                            <img className={styles.searchIconImage} src={SearchIcon} alt="search-icon" />
                                        </div>
                                    </div>
                                </div>

                                <div className={styles.locationUnits}>
                                    {!unitsListLoading && !unitsListError && !unitsData.length && (
                                        <div className={styles.noUnits}>
                                            <img src={NoUnits} alt="no-units" />
                                            <p className={styles.noUnitsFoundText}>No Units Found</p>
                                        </div>
                                    )}
                                    {/* Commented out Unitscard component - replaced with table view */}
                                    {/* <div className={styles.unitCard}>
                                        {unitsData?.length !== 0 &&
                                            unitsData?.map((unit: unitsDataInterface, index: number) => (
                                                <Unitscard
                                                    setShowToast={setShowToast}
                                                    setToastMessage={setToastMessage}
                                                    key={index}
                                                    unit={unit}
                                                    unitsData={unitsData}
                                                    lastUnitsRef={lastElement}
                                                    setUnitsData={setUnitsData}
                                                    onClick={() => onUnitClick(unit?.id, unit?.unit_status)}
                                                    locationId={id}
                                                    refetchUnits={refetchUnits}
                                                    setToastType={setToastType}
                                                />
                                            ))}
                                    </div> */}

                                    {/* Table View with Scroll Pagination */}
                                    {unitsData?.length > 0 && (
                                        <div className={styles.unitsTableWrapper}>
                                            <table className={styles.unitsTable}>
                                                <thead>
                                                    <tr>
                                                        <th>Sr. No.</th>
                                                        <th>Unit Name</th>
                                                        <th>Building</th>
                                                        <th>Price</th>
                                                        <th>Size</th>
                                                        <th>Beds</th>
                                                        <th>Baths</th>
                                                        {/* <th>Status</th> */}
                                                        <th>Available</th>
                                                        <th>Rent Ready</th>
                                                        <th>Vacant</th>
                                                        <th>Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {unitsData.map((unit: unitsDataInterface, index: number) => {
                                                        const isLastElement = index === unitsData.length - 1;
                                                        return (
                                                            <tr
                                                                key={unit.id}
                                                                ref={isLastElement ? (lastElement as any) : null}
                                                                className={`${styles.tableRow}`}
                                                                onClick={() => {
                                                                    if (unit?.unit_status === 'available') {
                                                                        onUnitClick(unit?.id, unit?.unit_status);
                                                                    }
                                                                }}>
                                                                <td>{index + 1}</td>
                                                                <td>
                                                                    <div className={styles.unitNameCell}>
                                                                        <span className={styles.unitId}>
                                                                            <span>
                                                                                {unit?.unitYardiId || unit?.name}
                                                                            </span>

                                                                            {/* badge if manually created and make it like tooltip*/}
                                                                            {
                                                                                unit?.integrationVendor === null && (
                                                                                    <Tooltip text='Manually created' variant='green' position='top'>
                                                                                        <span className={styles.badge}>M</span>
                                                                                    </Tooltip>
                                                                                )
                                                                            }

                                                                        </span>
                                                                        {unit?.nickName && (
                                                                            <span className={styles.unitNickname}>
                                                                                &quot;{unit?.nickName}&quot;
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    {unit?.buildingName ? (
                                                                        <span className={styles.buildingName}>{unit.buildingName}</span>
                                                                    ) : (
                                                                        <span className={styles.noBuilding}>—</span>
                                                                    )}
                                                                </td>
                                                                <td>
                                                                    {unit.price ? (
                                                                        <span className={styles.price}>$ {unit.price}</span>
                                                                    ) : (
                                                                        <span className={styles.noPrice}>—</span>
                                                                    )}
                                                                </td>
                                                                <td>
                                                                    <span className={styles.size}>{unit?.size || 0} Sq Ft</span>
                                                                </td>
                                                                <td>
                                                                    <span className={styles.beds}>
                                                                        {Number(unit?.rooms) === 0
                                                                            ? 'Studio'
                                                                            : `${Number(unit?.rooms) || 0} ${Number(unit?.rooms) === 1 ? 'Bed' : 'Beds'}`}
                                                                    </span>
                                                                </td>
                                                                <td>
                                                                    <span className={styles.baths}>
                                                                        {(+unit.baths || +unit.baths === 0) &&
                                                                            `${+unit.baths} ${+unit?.baths === 1 ? 'Bath' : 'Baths'}`}
                                                                    </span>
                                                                </td>


                                                                {/* Status icon for unit leased status */}
                                                                <td>
                                                                    <div className={styles.statusIconContainer}>
                                                                        {unit?.unitLeasedStatus?.toLowerCase() === 'available' ? (
                                                                            <div className={`${styles.statusIcon} ${styles.statusIconSuccess}`}>
                                                                                <CheckIcon width={16} height={16} fill="#ffffff" />
                                                                            </div>
                                                                        ) : (
                                                                            <div className={`${styles.statusIcon} ${styles.statusIconError}`}>
                                                                                <CrossIcon width={16} height={16} fill="#ffffff" />
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </td>

                                                                {/* Status icon for unit rent ready status */}
                                                                <td>
                                                                    <div className={styles.statusIconContainer}>
                                                                        {unit?.unitRentReady?.toLowerCase() === 'ready' || unit?.unitRentReady === 'true' ? (
                                                                            <div className={`${styles.statusIcon} ${styles.statusIconSuccess}`}>
                                                                                <CheckIcon width={16} height={16} fill="#ffffff" />
                                                                            </div>
                                                                        ) : (
                                                                            <div className={`${styles.statusIcon} ${styles.statusIconError}`}>
                                                                                <CrossIcon width={16} height={16} fill="#ffffff" />
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </td>

                                                                {/* Status icon for unit occupancy status */}
                                                                <td>
                                                                    <div className={styles.statusIconContainer}>
                                                                        {unit?.unit_status?.toLowerCase() === 'available' ? (
                                                                            <div className={`${styles.statusIcon} ${styles.statusIconSuccess}`}>
                                                                                <CheckIcon width={16} height={16} fill="#ffffff" />
                                                                            </div>
                                                                        ) : (
                                                                            <div className={`${styles.statusIcon} ${styles.statusIconError}`}>
                                                                                <CrossIcon width={16} height={16} fill="#ffffff" />
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </td>

                                                                {/* <td>
                                                                    <span
                                                                        className={`${styles.statusBadge} ${unit?.unit_status === 'available'
                                                                            ? styles.statusAvailable
                                                                            : unit?.unit_status === 'occupied'
                                                                                ? styles.statusOccupied
                                                                                : styles.statusArchived
                                                                            }`}>
                                                                        {unit?.unit_status === 'occupied' ? 'Occupied' : unit?.unit_status}
                                                                    </span>
                                                                </td> */}
                                                                <td>

                                                                    <div className={styles.actionButtons} onClick={(e) => e.stopPropagation()}>
                                                                        {canUpdateUnit && (
                                                                            <button
                                                                                className={styles.editBtn}
                                                                                onClick={() => {
                                                                                    setSelectedUnitForEdit(unit);
                                                                                    setEditUnitModal(true);
                                                                                }}>
                                                                                {/* <img src={iconEdit} alt="edit" /> */}
                                                                                <EditIcon width={20} height={20} stroke="#51ab86" strokeWidth={2} />
                                                                            </button>
                                                                        )}
                                                                        {canDeleteUnit && (
                                                                            <button
                                                                                className={styles.deleteBtn}
                                                                                onClick={() => {
                                                                                    setSelectedUnitForDelete(unit);
                                                                                    setDeleteUnitModal(true);
                                                                                }}>
                                                                                {/* <img src={iconDelete} alt="delete" /> */}
                                                                                <DeleteIcon width={20} height={20} stroke="#e74c3c" strokeWidth={2} />
                                                                            </button>
                                                                        )}
                                                                    </div>

                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                                {unitsListLoading && (
                                                    <tfoot>
                                                        <tr>
                                                            <td colSpan={11} ref={tinyLoaderRef} className={styles.tinyLoaderCell}>
                                                                <div className={styles.tinyLoader}>
                                                                    <TinyLoader />
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    </tfoot>
                                                )}
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </React.Fragment>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default LocationDetail;
